import { Element } from './types'
import { eventSystem } from 'modules-core/eventSystem'
import { filterByElementId } from './utility'
import { createValueCurve } from 'modules-core/audioEffectSystem/utility'
import {
	gainToVolume,
	volumeToGain,
} from 'modules-core/audioEffectSystem/volumeCurve'

// Register events.
eventSystem.add('setElementVolume')
eventSystem.add('setElementAdjustmentVolume')
eventSystem.add('setGlobalVolumeForElementAdjustment')

export type ElementVolumeApplyOptions = {
	/** Seconds; `0` snaps gain immediately instead of using the default fade. */
	fadeDuration?: number
	/** AudioContext timestamp for coordinated gain automation. */
	startTime?: number
}

export const INSTANT_ELEMENT_VOLUME_APPLY: ElementVolumeApplyOptions = {
	fadeDuration: 0,
}

interface Args {
	elementMap: Element.MutableMap
}
export const createElementVolumeSystem = ({ elementMap }: Args) => {

	const elementVolumeMap = new Map<number, number>()
	const elementAdjustmentVolumeMap = new Map<number, number>()
	let globalVolume = 1

	const adjustmentVolumeToSourceGain = (adjustmentVolume: number) => {
		const globalGain = volumeToGain(globalVolume)
		if (globalGain === 0) {
			return 1
		}
		return volumeToGain(globalVolume * adjustmentVolume) / globalGain
	}

	const adjustmentVolumeToSourceGainCurve = (
		adjustmentVolume: number,
		previousGlobalVolume: number,
		nextGlobalVolume: number,
	) => createValueCurve(
		volumeToGain(previousGlobalVolume),
		volumeToGain(nextGlobalVolume),
	).map((globalGain) => {
		if (globalGain === 0) {
			return 1
		}
		const semanticGlobalVolume = gainToVolume(globalGain)
		return volumeToGain(semanticGlobalVolume * adjustmentVolume) / globalGain
	})

	const applyElementIdAdjustmentVolume = (
		elementId: number,
		value: number,
		options?: ElementVolumeApplyOptions,
	) => {
		const sourceGain = adjustmentVolumeToSourceGain(value)
		filterByElementId(elementMap, elementId)
			.forEach(([, element]) => {
				element.adjustmentVolume = value
				if (options?.startTime === undefined) {
					element.setAdjustmentVolume(sourceGain, options?.fadeDuration)
				} else {
					element.setAdjustmentVolume(
						sourceGain,
						options?.fadeDuration,
						options.startTime,
					)
				}
			})
	}

	const applyElementIdAdjustmentVolumeCurve = (
		elementId: number,
		value: number,
		previousGlobalVolume: number,
		nextGlobalVolume: number,
		options: Required<ElementVolumeApplyOptions>,
	) => {
		const sourceGainCurve = adjustmentVolumeToSourceGainCurve(
			value,
			previousGlobalVolume,
			nextGlobalVolume,
		)
		filterByElementId(elementMap, elementId)
			.forEach(([, element]) => {
				element.adjustmentVolume = value
				if (element.setAdjustmentVolumeCurve) {
					element.setAdjustmentVolumeCurve(
						sourceGainCurve,
						options.fadeDuration,
						options.startTime,
					)
				} else {
					element.setAdjustmentVolume(
						sourceGainCurve[sourceGainCurve.length - 1],
						options.fadeDuration,
						options.startTime,
					)
				}
			})
	}

	const reapplyAdjustmentVolumes = (options?: ElementVolumeApplyOptions) => {
		elementAdjustmentVolumeMap.forEach((value, elementId) => {
			applyElementIdAdjustmentVolume(elementId, value, options)
		})
	}

	const reapplyAdjustmentVolumeCurves = (
		previousGlobalVolume: number,
		nextGlobalVolume: number,
		options: Required<ElementVolumeApplyOptions>,
	) => {
		elementAdjustmentVolumeMap.forEach((value, elementId) => {
			applyElementIdAdjustmentVolumeCurve(
				elementId,
				value,
				previousGlobalVolume,
				nextGlobalVolume,
				options,
			)
		})
	}

	eventSystem.add(
		'setGlobalVolumeForElementAdjustment',
		((event: CustomEvent) => {
			const previousGlobalVolume = globalVolume
			const nextGlobalVolume = Number(event.detail.volume)
			const fadeDuration = event.detail.fadeDuration
			const startTime = event.detail.startTime
			const options = { fadeDuration, startTime }
			globalVolume = nextGlobalVolume
			if (
				fadeDuration !== undefined
				&& fadeDuration !== 0
				&& startTime !== undefined
			) {
				reapplyAdjustmentVolumeCurves(
					previousGlobalVolume,
					nextGlobalVolume,
					{ fadeDuration, startTime },
				)
			} else {
				reapplyAdjustmentVolumes(options)
			}
		}) as EventListener,
	)

	const system = {
		adjustmentVolumeToSourceGain,
		elementVolumeMap,
		elementAdjustmentVolumeMap,
		setElementIdVolume: (elementId: number, value: number) => {
			if (elementVolumeMap.get(elementId) === value)
				return
			elementVolumeMap.set(elementId, value)
			filterByElementId(elementMap, elementId)
				.forEach(([, element]) => element.setVolume(value))
			eventSystem.dispatch('setElementVolume', {
				elementId,
				volume: value,
			})
		},
		setElementIdAdjustmentVolume: (
			elementId: number,
			value: number,
			options?: ElementVolumeApplyOptions,
		) => {
			const fadeDuration = options?.fadeDuration
			const isInstantApply = fadeDuration === 0
			const currentValue = elementAdjustmentVolumeMap.get(elementId)
			if (!isInstantApply && currentValue === value)
				return
			if (currentValue !== value) {
				elementAdjustmentVolumeMap.set(elementId, value)
				eventSystem.dispatch('setElementAdjustmentVolume', {
					elementId,
					volume: value,
				})
			}
			applyElementIdAdjustmentVolume(elementId, value, options)
		}
	}
	return system
}

export type ElementVolumeSystem = ReturnType<typeof createElementVolumeSystem>
